import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNearContext } from '../contexts/NearContext.jsx';

const normalizeWagerId = (wager) => {
  if (!wager) return null;
  return (
    wager.id ??
    wager.wager_id ??
    (typeof wager.request?.id !== 'undefined' ? wager.request.id : null)
  );
};

export const useActiveWagers = ({ from = 0, limit = 20, enabled = true } = {}) => {
  const { contractApi } = useNearContext();

  return useQuery(
    ['active-wagers', from, limit],
    async () => {
      const response = await contractApi.getActiveWagers(from, limit);
      return Array.isArray(response) ? response : [];
    },
    {
      enabled: Boolean(contractApi) && enabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useWager = (wagerId, { enabled = true } = {}) => {
  const { contractApi } = useNearContext();
  const numericId = typeof wagerId === 'string' ? parseInt(wagerId, 10) : wagerId;

  return useQuery(
    ['wager', numericId],
    async () => {
      if (Number.isNaN(numericId) || numericId == null) {
        return null;
      }
      return await contractApi.getWager(numericId);
    },
    {
      enabled: Boolean(contractApi) && enabled && numericId != null && !Number.isNaN(numericId),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useUserWagers = ({ accountId, from = 0, limit = 100, includeResolved = true } = {}) => {
  const { contractApi } = useNearContext();

  const userWagersQuery = useQuery(
    ['user-wagers', accountId, from, limit, includeResolved],
    async () => {
      if (!accountId) return [];
      const response = await contractApi.getUserWagers(accountId, from, limit, includeResolved);
      return Array.isArray(response) ? response : [];
    },
    {
      enabled: Boolean(contractApi) && Boolean(accountId),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const ids = useMemo(() => (userWagersQuery.data || []).map(normalizeWagerId).filter(Boolean), [userWagersQuery.data]);

  return {
    ...userWagersQuery,
    wagerIds: ids,
  };
};

export const getWagerIdentifier = normalizeWagerId;
